import { useSupportSheet } from "../../components/SupportSheet";
import { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AuraLayout, Button, Layout, SupportIcon } from "lola-framework-ui-test";
import { Toast } from "../../components/Toast";
import {
  GALLERY_NOTICE_FIXTURES,
  GalleryInboxChrome,
  GalleryInboxDrawer,
  GalleryPreferencesSheet,
} from "../../components/notifications/GalleryInbox";
import { TransactionsHistory } from "../../components/request/TransactionsHistory";
import { REQUEST_ROUTES } from "../../constants";
import { useStore } from "../../store/globalState";
import { useComposeStore } from "../../store/useComposeStore";
import { requestMockRequests } from "../../gallery/requestMock";
import type { ITransaction } from "../../interfaces/transactions";
import { BodyCopy } from "../../components/BodyCopy";
import { Navbar } from "../../components/Navbar";

/**
 * Requests hub. Lists the user's money requests (most recent first) and offers
 * a primary action to compose a new one. Gallery: the list comes from
 * `requestMockRequests` instead of `GET /widget/links/:id/requests`.
 *
 * Parity with production: navbar Back returns to Landing. "Back home" only
 * appears when a partner exit URL exists — the gallery has none, so the button
 * is omitted (same as a link without callbackUrl / sessionExpiredRedirectUrl).
 */
interface HomePageProps {
  originationDisabled?: boolean;
  inboxOpen?: boolean;
  toastOpen?: boolean;
  /** Gallery inspectable: open the preferences sheet on first paint. */
  preferencesOpen?: boolean;
}

export const HomePage = memo(({
  originationDisabled = false,
  inboxOpen = false,
  toastOpen = false,
  preferencesOpen: preferencesOpenProp = false,
}: HomePageProps) => {
  const { t } = useTranslation("request");
  const { theme } = useStore();
  const navigate = useNavigate();
  const { openSupport, supportSheet } = useSupportSheet("request");
  const { resetCompose } = useComposeStore();

  const colorConfig = theme.whiteView;
  const { title, backgroundBtn, textColorBtn, dropzoneColors, bodyCopy } = colorConfig;
  const showBell = !originationDisabled;
  const [noticesOpen, setNoticesOpen] = useState(inboxOpen);
  const [preferencesOpen, setPreferencesOpen] = useState(preferencesOpenProp);
  const [readIds, setReadIds] = useState<string[]>([]);
  const [archivedIds, setArchivedIds] = useState<string[]>([]);
  const notices = GALLERY_NOTICE_FIXTURES.filter(
    (item) => !archivedIds.includes(item.id),
  ).map((item) => ({
    id: item.id,
    title: t(item.titleKey),
    body: t(item.bodyKey),
    isRead: item.read || readIds.includes(item.id),
    createdAt: item.createdAt,
  }));

  const items = useMemo<ITransaction[]>(
    () =>
      requestMockRequests.slice(0, 5).map((item) => ({
        ...item,
        onSelect:
          item.requestId
            ? () =>
                navigate(`${REQUEST_ROUTES.RECEIPT}/${item.requestId}`)
            : undefined,
      })),
    [navigate]
  );

  const handleNewRequest = useCallback(() => {
    if (originationDisabled) return;
    resetCompose();
    navigate(REQUEST_ROUTES.TRANSFER);
  }, [originationDisabled, resetCompose, navigate]);

  return (
    <>
    <AuraLayout
      colorConfig={colorConfig}
      className={
        showBell
          ? "need-help-header notifications-home-header"
          : "need-help-header"
      }
    >
      <Layout.Header>
        <Navbar
          title={showBell ? undefined : t("common.needHelp")}
          align="right"
          onBackClick={() => navigate(REQUEST_ROUTES.LANDING)}
          onIconClick={showBell ? undefined : openSupport}
          icon={
            showBell ? (
              <GalleryInboxChrome
                ns="request"
                unread={notices.filter((item) => !item.isRead).length}
                bellColor={title}
                badgeBackground={backgroundBtn}
                badgeTextColor={textColorBtn}
                onOpen={() => setNoticesOpen(true)}
              />
            ) : (
              <SupportIcon size={35} colors={dropzoneColors} />
            )
          }
        />
      </Layout.Header>
      <Layout.Content className="transactions-section">
        <TransactionsHistory items={items} />
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section transactions-footer">
          {originationDisabled && (
            <BodyCopy
              textColor={bodyCopy}
              className="transactions-disabled-note"
            >
              {t("transactions.requestsDisabled")}
            </BodyCopy>
          )}
          <Button
            size="large"
            background={backgroundBtn}
            color={textColorBtn}
            disabled={originationDisabled}
            onClick={handleNewRequest}
          >
            {t("transactions.newRequest")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
    {supportSheet}
      {showBell ? (
        <>
        <GalleryInboxDrawer
          ns="request"
          items={notices}
          open={noticesOpen}
          onClose={() => setNoticesOpen(false)}
          onMarkRead={(id) =>
            setReadIds((ids) => (ids.includes(id) ? ids : [...ids, id]))
          }
          onArchive={(id) =>
            setArchivedIds((ids) => (ids.includes(id) ? ids : [...ids, id]))
          }
          onOpenPreferences={() => {
            setNoticesOpen(false);
            setPreferencesOpen(true);
          }}
        />
        <GalleryPreferencesSheet
          ns="request"
          open={preferencesOpen}
          onClose={() => setPreferencesOpen(false)}
        />
        </>
      ) : null}
      {toastOpen ? (
        <Toast message={t("notifications.exampleToast")} type="info" persist sound />
      ) : null}
  </>
  );
});

export const UserRestrictedHomePage = memo(() => (
  <HomePage originationDisabled />
));

export const InboxPage = memo(() => <HomePage inboxOpen />);

export const PreferencesPage = memo(() => <HomePage preferencesOpen />);

export const ToastPage = memo(() => <HomePage toastOpen />);
